import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Navigate } from "react-router-dom";
import { Auth, authorizeSelectors } from "./store/authorize";
import { useSelector } from "./store/store";
import { useSignal } from "./hooks/signal";
import { SignalConnectionState } from "./services/signal";
import { applyAuth, clearAuth, bootstrapAuth } from "./auth/authState";
import { onNetworkStatus } from "./auth/authClient";

// The HTTP layer (instances + interceptors + single-flight refresh) lives in the framework-free auth module.
// Re-exported here so the existing `import { http } from "../Guard"` call sites keep working.
export { http, publicHttp } from "./auth/authClient";

// Guard Context - Storage for guard state

interface Context {
  // Auth
  readonly loading?: boolean;
  readonly authorized?: boolean;
  readonly nointernet?: boolean;
  readonly keepLoggedIn?: boolean;
  readonly signIn: (user: Auth, keepLoggedIn: boolean) => void;
  readonly signOut: () => void;

  // Hub
  readonly on: (name: string, fn: (...args: any[]) => void) => void;
  readonly off: (name: string, fn: (...args: any[]) => void) => void;
  readonly send: (name: string, ...args: any[]) => void;
  // Connection liveness as an external store (consume via useSyncExternalStore): keeps the context
  // value stable across hub transitions so they don't re-render unrelated consumers.
  readonly subscribeConnection: (listener: () => void) => () => void;
  readonly getConnectionState: () => SignalConnectionState;
}

const GuardContext = React.createContext<Context>(null!);

// Guard hook - Get guard state anywhere in your app

export function useGuard() {
  return React.useContext(GuardContext);
}

// Guard Provider - Provider to keep state of guard for all app

export interface GuardProviderProps {}

export function GuardProvider(props: PropsWithChildren<GuardProviderProps>) {
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [keepLoggedIn, setKeepLoggedIn] = useState(false);
  const [nointernet, setNointernet] = useState(false);
  const auth = useSelector(authorizeSelectors.auth);
  // useSignal re-renders this provider on every hub transition (connect / reconnecting /
  // reconnected / close), so reading the live state here keeps it reactive.
  const [signal] = useSignal(`${process.env.REACT_APP_API_HOST!}/apphub`);

  // Hydrate auth from storage once (populates the module authStore + Redux). The interceptors are already wired at
  // module load in auth/authClient.
  useEffect(() => {
    bootstrapAuth();
  }, []);

  // Mirror the module's network-status into React (the interceptor no longer touches React state directly).
  useEffect(() => onNetworkStatus(setNointernet), []);

  useEffect(() => {
    setLoading(false);
    setAuthorized(auth?.token != null);
  }, [auth]);

  // Bring the hub up exactly once when the user becomes authorized (and down on logout). Keyed off
  // `authorized` only — NOT the whole `auth` object — so token refreshes / auth-object churn during
  // login don't close+reopen the connection (which bounced the greeting push and flickered the UI).
  // `signal.start` reads the live token lazily via accessTokenFactory, so no token arg is needed.
  useEffect(() => {
    if (authorized) {
      signal.start();
    } else {
      signal.close();
    }

    return () => {
      signal.close();
    };
  }, [authorized, signal]);

  const signIn = useCallback((auth: Auth, keepLoggedIn: boolean) => {
    applyAuth(auth, keepLoggedIn);
    setKeepLoggedIn(keepLoggedIn);
  }, []);

  const signOut = useCallback(() => {
    clearAuth();
  }, []);

  // Hub helpers delegate to the stable Signal instance, so they can be stable references too.
  const on = useCallback(
    (name: string, fn: (...args: any[]) => void) => signal.on(name, fn),
    [signal],
  );
  const off = useCallback(
    (name: string, fn: (...args: any[]) => void) => signal.off(name, fn),
    [signal],
  );
  const send = useCallback(
    (name: string, ...args: any[]) => signal.send(name, ...args),
    [signal],
  );

  const subscribeConnection = useCallback(
    (listener: () => void) => signal.subscribeConnection(listener),
    [signal],
  );
  const getConnectionState = useCallback(() => signal.connectionState, [signal]);

  // Memoise the context value: consumers no longer re-render on every provider render, and
  // `guard` / `on` / `off` stay stable references a subscription effect can depend on safely.
  // Connection liveness is an external store (above), so this value is stable across hub
  // transitions — they re-render only the liveness indicator, not every consumer.
  const value = useMemo<Context>(
    () => ({
      authorized,
      nointernet,
      keepLoggedIn,
      loading,
      signIn,
      signOut,
      on,
      off,
      send,
      subscribeConnection,
      getConnectionState,
    }),
    [
      authorized,
      nointernet,
      keepLoggedIn,
      loading,
      signIn,
      signOut,
      on,
      off,
      send,
      subscribeConnection,
      getConnectionState,
    ],
  );

  return (
    <GuardContext.Provider value={value}>
      {props.children}
    </GuardContext.Provider>
  );
}

// Guard - Wrap root page which must be public or protected

interface GuardProps {
  readonly public?: boolean;
  readonly redirectTo?: string;
}

function Guard(props: PropsWithChildren<GuardProps>) {
  const { authorized, loading } = useGuard();

  if ((props.public && authorized) || (!props.public && !authorized)) {
    return <>{!loading && <Navigate to={props.redirectTo ?? "/"} replace />}</>;
  }

  return <>{props.children}</>;
}

export default Guard;
